import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import {
  dayString,
  downloadCsv,
  getInstructorLoad,
  TLoadWindow,
} from '../../../requests/opsReports';
import {
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  tableClass,
  tdClass,
  thClass,
  theadClass,
} from '../ui';

const num = (n: number) => n.toLocaleString();
const hrs = (n: number) => (Math.round(n * 100) / 100).toLocaleString();
const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function preset(kind: 'next' | 'this' | 'last30'): TLoadWindow {
  const now = new Date();
  if (kind === 'next') return {};
  if (kind === 'this') {
    return {
      from: dayString(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: dayString(new Date(now.getFullYear(), now.getMonth() + 1, 1)),
    };
  }
  return {
    from: dayString(new Date(now.getTime() - 30 * 864e5)),
    to: dayString(new Date(now.getTime() + 864e5)),
  };
}

/**
 * Classes, registrations and hours per instructor for a window — the
 * PD / BA allocation and month-end balance views. Counts only; rows are
 * in name order and stay that way.
 */
const InstructorLoad: React.FC = () => {
  const [draft, setDraft] = useState<TLoadWindow>({});
  const [window, setWindow] = useState<TLoadWindow>({});
  const [busy, setBusy] = useState(false);
  const [showCost, setShowCost] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['opsInstructorLoad', window],
    queryFn: () => getInstructorLoad(window),
  });

  const apply = (w: TLoadWindow) => {
    setDraft(w);
    setWindow(w);
  };

  const csv = async () => {
    setBusy(true);
    try {
      await downloadCsv(
        'ops/reports/instructor-load',
        window,
        `instructor-load-${data?.window.from ?? 'window'}.csv`,
      );
    } catch (e) {
      showToast(resolveApiMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <div className={labelClass}>From</div>
          <input
            type="date"
            className={inputClass}
            value={draft.from ?? ''}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
        </div>
        <div>
          <div className={labelClass}>To (exclusive)</div>
          <input
            type="date"
            className={inputClass}
            value={draft.to ?? ''}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
        </div>
        <button className={btnSecondary} onClick={() => setWindow(draft)}>
          Apply
        </button>
        <span className="mx-1 text-gray-two">·</span>
        <button className={btnSecondary} onClick={() => apply(preset('next'))}>
          Next month
        </button>
        <button className={btnSecondary} onClick={() => apply(preset('this'))}>
          This month
        </button>
        <button
          className={btnSecondary}
          onClick={() => apply(preset('last30'))}
        >
          Last 30 days
        </button>
        <span className="flex-1" />
        <button className={btnSecondary} onClick={csv} disabled={busy || !data}>
          {busy ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-500 bg-red-100 px-4 py-2 text-sm text-red-one">
          {resolveApiMessage(error)}
        </div>
      )}

      {data && (
        <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat
            value={`${data.window.from} → ${data.window.to}`}
            label={
              data.window.label === 'next month'
                ? 'window · next month'
                : 'window'
            }
            small
          />
          <Stat value={num(data.totals.classes)} label="classes" />
          <Stat value={num(data.totals.registrations)} label="registrations" />
          <Stat value={hrs(data.totals.hours)} label="hours" />
        </div>
      )}

      {data && (
        <Card
          className="mb-4"
          title={`Instructor cost in the window · ${money(data.cost.total)}`}
          right={
            <button
              className="text-xs text-blue-primary"
              onClick={() => setShowCost((v) => !v)}
            >
              {showCost ? 'Hide per-class lines' : 'Show per-class lines'}
            </button>
          }
        >
          <div className="px-5 py-3 text-xs text-gray-three">
            Hours × rate per assignment, summed over the window —{' '}
            {data.cost.classesWithAssignments} of {data.totals.classes} classes
            have an assignment to cost
            {data.cost.classesDefaulted > 0 &&
              `; ${data.cost.classesDefaulted} used the default rate because no rate is on file`}
            . {data.cost.basis}. A total and per-class lines only; the
            per-assignment breakdown lives on each class.
          </div>
          {showCost && data.cost.lines.length > 0 && (
            <div className="overflow-x-auto border-t border-white-three">
              <table className={`${tableClass} min-w-[700px]`}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>Class</th>
                    <th className={thClass}>Date</th>
                    <th className={thClass}>Type</th>
                    <th className={`${thClass} text-right`}>Assignments</th>
                    <th className={`${thClass} text-right`}>Hours</th>
                    <th className={`${thClass} text-right`}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cost.lines.map((l) => (
                    <tr key={l.classId}>
                      <td className={`${tdClass} font-medium text-gray-four`}>
                        {l.className ?? l.classId}
                      </td>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {l.start?.slice(0, 10) ?? '—'}
                      </td>
                      <td className={tdClass}>{l.classType ?? '—'}</td>
                      <td className={`${tdClass} text-right`}>
                        {l.assignments}
                      </td>
                      <td className={`${tdClass} text-right`}>
                        {hrs(l.hours)}
                      </td>
                      <td
                        className={`${tdClass} text-right [font-variant-numeric:tabular-nums]`}
                      >
                        {money(l.amount)}
                        {l.defaulted && (
                          <span
                            className="ml-1 text-xs text-gray-three"
                            title="No rate on file for at least one assignment; the default was used"
                          >
                            default
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : data && data.items.length === 0 ? (
        <Card>
          <EmptyState
            title="No classes in this window"
            hint="Widen the range or pick another month."
          />
        </Card>
      ) : (
        data && (
          <Card
            title="Per instructor, in name order"
            right={
              <span className="text-xs text-gray-three">
                {data.basis.hours}
              </span>
            }
          >
            <div className="overflow-x-auto">
              <table className={`${tableClass} min-w-[1000px]`}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>Instructor</th>
                    <th className={`${thClass} text-right`}>Classes</th>
                    <th className={`${thClass} text-right`}>Registrations</th>
                    <th className={`${thClass} text-right`}>Hours</th>
                    <th className={`${thClass} text-right`}>Public</th>
                    <th className={`${thClass} text-right`}>Private</th>
                    <th className={`${thClass} text-right`}>PD</th>
                    <th className={`${thClass} text-right`}>BA</th>
                    <th className={`${thClass} text-right`}>Other</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.instructorName}>
                      <td className={`${tdClass} font-medium text-gray-four`}>
                        {r.instructorName}
                      </td>
                      <td className={`${tdClass} text-right`}>
                        {num(r.classes)}
                      </td>
                      <td className={`${tdClass} text-right`}>
                        {num(r.registrations)}
                      </td>
                      <td className={`${tdClass} text-right font-medium`}>
                        {hrs(r.hours)}
                      </td>
                      <Split c={r.public} />
                      <Split c={r.private} />
                      <Split c={r.PD} />
                      <Split c={r.BA} />
                      <Split c={r.other} />
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-grey-50 text-sm font-semibold text-gray-four">
                  <tr>
                    <td className={tdClass}>Total</td>
                    <td className={`${tdClass} text-right`}>
                      {num(data.totals.classes)}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {num(data.totals.registrations)}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {hrs(data.totals.hours)}
                    </td>
                    <td className={tdClass} colSpan={5} />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="border-t border-white-three px-5 py-3 text-xs text-gray-three">
              Each split shows classes · hours.{' '}
              {data.unassigned.classes > 0 && (
                <>
                  {num(data.unassigned.classes)}{' '}
                  {data.unassigned.classes === 1 ? 'class' : 'classes'} (
                  {hrs(data.unassigned.hours)} h) in this window have no
                  instructor yet and are counted in the totals only.{' '}
                </>
              )}
              {data.basis.registrations}.
            </div>
          </Card>
        )
      )}
    </>
  );
};

const Split: React.FC<{ c: { classes: number; hours: number } }> = ({ c }) => (
  <td className={`${tdClass} whitespace-nowrap text-right text-gray-three`}>
    {c.classes === 0 ? (
      <span className="text-gray-two">—</span>
    ) : (
      <>
        <span className="text-gray-four">{num(c.classes)}</span>
        <span className="mx-1 text-gray-two">·</span>
        {hrs(c.hours)} h
      </>
    )}
  </td>
);

const Stat: React.FC<{ value: string; label: string; small?: boolean }> = ({
  value,
  label,
  small,
}) => (
  <div className="rounded-xl border border-white-regular bg-white px-5 py-4">
    <div
      className={`${
        small ? 'text-base' : 'text-3xl'
      } font-bold text-gray-four [font-variant-numeric:tabular-nums]`}
    >
      {value}
    </div>
    <div className="mt-0.5 text-sm text-gray-three">{label}</div>
  </div>
);

export default InstructorLoad;
